/**
 * Step 7 — Doctrinal trust — SYNTHESIS variant
 *
 * Spec: specs/onboarding-rebuild.md → Act 2 step 7 (NEW 2026-05-13)
 *
 * Mason lock 2026-05-14: copy-preserving visual restructure. Same 4
 * promises, but rendered as an iconified pillar list (not a wall of
 * centered prose). Eye gets anchors via icons + hairline rules between
 * rows. Pastor / local-church line keeps the gold-italic standout
 * weight as the climax of the list.
 */
import { useEffect, useState } from 'react'
import ContinueButton from '@/components/onboarding/ContinueButton'

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'

// 4 trust pillars. Each has an icon glyph + the promise text. The
// `accent: true` row gets gold-italic treatment (the load-bearing line).
// Mason lock 2026-05-14: "Is / Is not" framing instead of "Won't" — the
// AI is a tool, not a moral agent making promises. Each line describes
// what Illuminate IS or ISN'T, which is honest and theologically careful.
const PILLARS = [
  {
    icon: 'creed',
    text: 'Is built on the historic Christian creeds.',
  },
  {
    icon: 'spirit',
    text: 'Is not the Holy Spirit.',
  },
  {
    icon: 'church',
    text: 'Is not a replacement for your pastor or local church.',
    accent: true,
  },
  {
    icon: 'verse',
    text: 'Is a tool — Scripture stays the standard.',
  },
]

// Module-scope SVG icon set. Stroke-based, gold or muted depending on
// row state. Keeps the row visually consistent — every pillar gets a
// 28×28 glyph slot, label sits beside.
function PillarIcon({ kind, accent }) {
  const stroke = accent ? 'var(--gold)' : 'var(--gold-dim)'
  const props = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke,
    strokeWidth: 1.6,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  }
  if (kind === 'creed') {
    // Open scroll / tablet — historic creeds.
    return (
      <svg {...props}>
        <path d="M5 4h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2z" />
        <path d="M8 8h8M8 12h8M8 16h5" />
      </svg>
    )
  }
  if (kind === 'spirit') {
    // Dove silhouette — Holy Spirit.
    return (
      <svg {...props}>
        <path d="M4 13c2-1 4-3 6-3 3 0 5 2 9 1-1 3-4 6-8 6-4 0-6-2-7-4z" />
        <path d="M14 7c0-1.5 1-3 3-3" />
      </svg>
    )
  }
  if (kind === 'church') {
    // Steepled chapel — local church.
    return (
      <svg {...props}>
        <path d="M12 3v6" />
        <path d="M10 5h4" />
        <path d="M5 21V11l7-4 7 4v10" />
        <path d="M10 21v-5h4v5" />
      </svg>
    )
  }
  // verse — open Bible / book with bookmark
  return (
    <svg {...props}>
      <path d="M4 5a2 2 0 0 1 2-2h12v17H6a2 2 0 0 0-2 2z" />
      <path d="M4 5v17" />
      <path d="M14 3v8l-2-1.5L10 11V3" />
    </svg>
  )
}

export default function DoctrinalTrustSynthesis({ onContinue }) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [])

  // Cascade enter — header first, then pillars staggered, then CTA.
  const enter = i => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(8px)',
    transition: `opacity 600ms ${EASE} ${80 + i * 90}ms, transform 600ms ${EASE} ${80 + i * 90}ms`,
  })

  return (
    <div className="w-full max-w-lg md:max-w-xl lg:max-w-2xl mx-auto px-6 py-12 sm:py-16">
      <style>{`
        .dt-syn-cta {
          transition: transform 220ms ${EASE}, box-shadow 220ms ${EASE}, background-color 220ms ${EASE};
        }
        .dt-syn-cta:hover {
          transform: translateY(-1px);
          box-shadow: 0 14px 36px -16px rgba(212,175,90,0.55);
        }
        .dt-syn-cta:active { transform: translateY(0); }
        .dt-syn-cta:focus-visible {
          outline: none;
          box-shadow: 0 0 0 2px var(--bg-base), 0 0 0 4px var(--gold);
        }
      `}</style>

      {/* Heading — promoted from prior eyebrow. Mason lock 2026-05-14:
          dropped the separate "Before we go further" line; "Where
          Illuminate stands" now carries the framing on its own. */}
      <h1
        className="font-heading text-[28px] sm:text-[32px] leading-[1.15] text-[var(--text-primary)] tracking-[-0.005em] text-center"
        style={enter(0)}
      >
        Where Illuminate stands.
      </h1>

      {/* Pillar list — 4 rows, hairline rules between, icon on the left,
          text on the right. Accent row (church) gets gold-italic serif
          treatment so the eye lands on the load-bearing promise. */}
      <ul className="mt-9 mx-auto max-w-md space-y-0">
        {PILLARS.map((pillar, idx) => (
          <li
            key={pillar.icon}
            className="flex items-center gap-4 py-4"
            style={{
              ...enter(1 + idx),
              borderTop: idx === 0 ? 'none' : '1px solid var(--gold-dim)',
              borderTopColor: idx === 0 ? undefined : 'rgba(212, 168, 67, 0.18)',
            }}
          >
            <span
              className="flex-shrink-0 inline-flex items-center justify-center"
              style={{ width: 28, height: 28 }}
            >
              <PillarIcon kind={pillar.icon} accent={pillar.accent} />
            </span>
            <p
              className={
                pillar.accent
                  ? 'text-[17px] sm:text-[19px] italic leading-[1.4] text-[var(--gold)]'
                  : 'text-[15px] sm:text-[16px] leading-[1.55] text-[var(--text-primary)]'
              }
              style={pillar.accent ? { fontFamily: 'var(--font-heading)' } : undefined}
            >
              {pillar.text}
            </p>
          </li>
        ))}
      </ul>

      {/* Continue — migrated to shared ContinueButton 2026-05-17 */}
      <div className="mt-12 flex justify-center" style={enter(1 + PILLARS.length)}>
        <ContinueButton onClick={onContinue}>Continue</ContinueButton>
      </div>
    </div>
  )
}
